import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleNotch } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { apiSend } from "@/lib/api-error";
import { formatCurrency, formatDateTimeBs } from "@/lib/format";
import { toast } from "@/lib/toast";
import type { RefundObligation } from "@/types";
import { KIND_LABEL, METHOD_LABEL, reasonLabel } from "./refund-labels";
import { ResolveObligationDialog } from "./RefundDialogs";

type State = "open" | "resolved";

function contextLine(o: RefundObligation): string | null {
  if (o.groupName) return o.groupName;
  if (o.scheduledAt) return `Termin ${formatDateTimeBs(o.scheduledAt)}`;
  return null;
}

export function RefundObligationsSection() {
  const queryClient = useQueryClient();
  const [state, setState] = useState<State>("open");
  const [resolving, setResolving] = useState<RefundObligation | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["admin", "refunds", state],
    queryFn: () =>
      fetch(`/api/admin/refunds?state=${state}`, { credentials: "include" })
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
        .then((p) => p.items as RefundObligation[]),
  });

  const resolveMutation = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) =>
      apiSend(`/api/admin/refunds/${id}/resolve`, "POST", { note: note || null }, "Greška pri označavanju povrata"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin", "refunds"] });
      setResolving(null);
      toast.success("Povrat je označen kao vraćen");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const items = data ?? [];
  const openTotal = state === "open" ? items.reduce((sum, o) => sum + o.amountKm, 0) : 0;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold text-foreground">Povrati klijentima</h2>
          <p className="text-sm text-muted-foreground">
            Novac koji treba vratiti ručno. Nakon povrata u Monri portalu ili bankovnim nalogom označite ga ovdje.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {(["open", "resolved"] as const).map((s) => (
            <Button key={s} size="sm" variant={state === s ? "default" : "outline"} onClick={() => setState(s)}>
              {s === "open" ? "Otvoreni" : "Vraćeni"}
            </Button>
          ))}
          {isLoading && <CircleNotch className="size-4 animate-spin text-muted-foreground" />}
        </div>
      </div>

      {state === "open" && items.length > 0 && (
        <p className="text-sm text-muted-foreground">
          Ukupno za povrat: <span className="font-medium text-foreground">{formatCurrency(openTotal)}</span>
        </p>
      )}

      {items.length === 0 && !isLoading ? (
        <div className="rounded-2xl border border-dashed border-border/60 bg-card py-12 text-center text-sm text-muted-foreground">
          {state === "open" ? "Nema otvorenih povrata." : "Još nema vraćenih povrata."}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-border/60 bg-card">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Vrsta</th>
                <th className="px-4 py-3">Klijent</th>
                <th className="px-4 py-3">Terapeut</th>
                <th className="px-4 py-3">Iznos</th>
                <th className="px-4 py-3">Razlog</th>
                <th className="px-4 py-3">{state === "open" ? "Nastalo" : "Vraćeno"}</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {items.map((o) => {
                const context = contextLine(o);
                return (
                  <tr key={o.id} className="border-t border-border/50 align-top">
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{KIND_LABEL[o.kind] ?? o.kind}</div>
                      {context && <div className="text-xs text-muted-foreground">{context}</div>}
                    </td>
                    <td className="px-4 py-3">
                      <div>{o.clientName ?? "Nepoznat"}</div>
                      {o.clientEmail && <div className="text-xs text-muted-foreground">{o.clientEmail}</div>}
                    </td>
                    <td className="px-4 py-3">{o.therapistName ?? ""}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-foreground">{formatCurrency(o.amountKm)}</div>
                      <div className="text-xs text-muted-foreground">{METHOD_LABEL[o.method ?? ""] ?? "Nepoznat način"}</div>
                    </td>
                    <td className="max-w-[240px] px-4 py-3 text-muted-foreground" title={o.details ?? o.reason}>
                      {reasonLabel(o.reason)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {state === "open" ? (
                        formatDateTimeBs(o.createdAt)
                      ) : (
                        <>
                          <div>{formatDateTimeBs(o.resolvedAt ?? o.createdAt)}</div>
                          {o.resolvedByName && <div className="text-xs">{o.resolvedByName}</div>}
                          {o.resolutionNote && (
                            <div className="max-w-[200px] truncate text-xs" title={o.resolutionNote}>
                              {o.resolutionNote}
                            </div>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {state === "open" && (
                        <Button size="sm" variant="outline" onClick={() => setResolving(o)}>
                          Označi vraćeno
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {resolving && (
        <ResolveObligationDialog
          clientName={resolving.clientName}
          amountKm={resolving.amountKm}
          onClose={() => setResolving(null)}
          onConfirm={(note) => resolveMutation.mutate({ id: resolving.id, note })}
          isPending={resolveMutation.isPending}
        />
      )}
    </section>
  );
}
